import { useState } from "react";
import { motion } from "framer-motion";
import { ExternalLink, ArrowUpRight } from "lucide-react";

const PortfolioSection = ({ portfolio }) => {
  const [filter, setFilter] = useState("Todos");
  const categories = ["Todos", ...new Set(portfolio.map((p) => p.category))];

  const filteredPortfolio =
    filter === "Todos"
      ? portfolio
      : portfolio.filter((p) => p.category === filter);

  return (
    <section
      id="portafolio"
      className="py-24 md:py-32 bg-[#0A0A0A] relative"
      data-testid="portfolio-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#00E5FF] mb-4 block">
            Portafolio
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white mb-4">
            Proyectos que{" "}
            <span className="gradient-text">generan resultados</span>
          </h2>
          <p className="text-zinc-400 max-w-2xl mx-auto text-lg">
            Cada proyecto representa una historia de éxito y transformación digital
          </p>
        </motion.div>

        {/* Filter Tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setFilter(category)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                filter === category
                  ? "bg-gradient-to-r from-[#2979FF] to-[#00E5FF] text-white"
                  : "bg-white/5 text-zinc-400 hover:bg-white/10 hover:text-white border border-white/5"
              }`}
              data-testid={`portfolio-filter-${category.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Portfolio Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPortfolio.map((project, index) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              className="portfolio-item rounded-3xl overflow-hidden bg-[#0A0A0A] border border-white/5 group cursor-pointer"
              data-testid={`portfolio-item-${project.id}`}
            >
              {/* Image */}
              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  loading="lazy"
                />
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                {/* Content */}
                <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                  <span className="text-xs text-[#00E5FF] font-medium uppercase tracking-wider mb-2 block">
                    {project.category}
                  </span>
                  <h3 className="text-xl font-semibold text-white mb-2">
                    {project.title}
                  </h3>
                  <p className="text-zinc-400 text-sm mb-4">
                    {project.description}
                  </p>
                  {project.url ? (
                    <a 
                      href={project.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-white text-sm font-medium hover:text-[#00E5FF] transition-colors"
                    >
                      Ver sitio web
                      <ArrowUpRight className="w-4 h-4" />
                    </a>
                  ) : (
                    <span className="inline-flex items-center gap-2 text-white text-sm font-medium">
                      Ver proyecto
                      <ArrowUpRight className="w-4 h-4" />
                    </span>
                  )}
                </div>
              </div>
              
              {/* Always visible info for mobile */}
              <div className="p-4 md:hidden">
                <span className="text-xs text-[#00E5FF] font-medium uppercase tracking-wider mb-1 block">
                  {project.category}
                </span>
                <h3 className="text-lg font-semibold text-white mb-1">
                  {project.title}
                </h3>
                <p className="text-zinc-500 text-sm mb-3">
                  {project.description}
                </p>
                {project.url && (
                  <a 
                    href={project.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-[#00E5FF] text-sm font-medium"
                  >
                    Ver sitio web
                    <ArrowUpRight className="w-4 h-4" />
                  </a>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        {/* No results message */}
        {filteredPortfolio.length === 0 && (
          <div className="text-center py-12">
            <p className="text-zinc-500">No hay proyectos en esta categoría</p>
          </div>
        )}

        {/* View All CTA */}
        <div className="text-center mt-12">
          <button
            className="inline-flex items-center gap-2 text-white bg-white/5 border border-white/10 hover:border-white/20 px-6 py-3 rounded-full font-medium transition-all duration-300"
            data-testid="portfolio-view-all"
          >
            Ver todos los proyectos
            <ExternalLink className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default PortfolioSection;
